import {
  RunningUploadsJob,
  RunningUploadsRecord,
  UploadSequence,
} from 'types/RunningUpload';
import { createRunningUploadsFromFiles } from 'app/utils/runningUploads/runningUploadsFiles';

/**
 * Where uploaded files land. `legacy` is master's path (the old app's
 * `/gallery/ajaxJustUpload` for images, media-upload-service for videos,
 * `/media-library/assets/upload` for documents); `assets` is the Media
 * Library's register → S3 → finalize path.
 */
export type UploadDestination =
  | { library: 'legacy'; albumId?: number }
  | { library: 'assets'; albumId?: string };

/**
 * Master uploads to the legacy endpoints one file at a time: its callers asked
 * for `fifo` where the branch now asks for `parallel`, and the default was
 * `lifo`. Only the asset path (direct to S3) runs uploads in parallel.
 */
export function resolveUploadSequence(
  destination: UploadDestination,
  requested: UploadSequence | undefined,
): UploadSequence {
  if (destination.library === 'assets') {
    return requested ?? 'parallel';
  }

  if (requested === 'parallel') {
    return 'fifo';
  }

  return requested ?? 'lifo';
}

export type CreateRunningUploadsParams = {
  upload: {
    files: { videos: File[]; images: File[]; documents: File[] };
  };
  options: {
    pageId: number;
    destination: UploadDestination;
    userAccountPublicId: string;
    userId: number;
    uploadSequence: UploadSequence;
  };
};

export type CreateRunningUploadsReturn = {
  runningUploads: RunningUploadsRecord;
  jobs: RunningUploadsJob[];
};

export async function createRunningUploads({
  upload,
  options,
}: CreateRunningUploadsParams): Promise<CreateRunningUploadsReturn> {
  let runningUploads: RunningUploadsRecord = {};
  let jobs: RunningUploadsJob[] = [];

  function appendUploads(newUploads: CreateRunningUploadsReturn) {
    runningUploads = {
      ...runningUploads,
      ...newUploads.runningUploads,
    };

    jobs = [...jobs, ...newUploads.jobs];
  }

  if (upload.files) {
    const fileRunningUploads = await createRunningUploadsFromFiles({
      files: upload.files,
      ...options,
    });

    appendUploads(fileRunningUploads);
  }

  return {
    runningUploads,
    jobs,
  };
}
